/**
 * Token storage utility with validation and security measures
 */

const TOKEN_KEY = 'token';
const USER_DATA_KEY = 'userData';
const REDIRECT_PATH_KEY = 'redirectPath';
const LANGUAGE_KEY = 'i18nextLng'; // same key i18next uses for localStorage

/**
 * Validates if a token string is in a valid format
 * @param {string} token - Token to validate
 * @returns {boolean} - True if token appears valid
 */
const isValidTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  // Basic validation: token should be non-empty string
  return token.trim().length > 0;
};

/**
 * Safely stores token in localStorage
 * @param {string} token - Token to store
 * @returns {boolean} - True if stored successfully
 */
export const setToken = (token) => {
  try {
    if (!isValidTokenFormat(token)) {
      return false;
    }
    localStorage.setItem(TOKEN_KEY, token);
    return true;
  } catch (error) {
    // localStorage might be disabled or full
    return false;
  }
};

/**
 * Safely retrieves token from localStorage
 * @returns {string|null} - Token or null if not found/invalid
 */
export const getToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!isValidTokenFormat(token)) {
      return null;
    }
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Removes token from localStorage
 */
export const removeToken = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    // Ignore errors during cleanup
  }
};

/**
 * Safely stores user data in localStorage. Merges preferredLanguage from current i18n if not present.
 * @param {object} userData - User data to store
 * @returns {boolean} - True if stored successfully
 */
export const setUserData = (userData) => {
  try {
    if (!userData || typeof userData !== 'object') {
      return false;
    }
    const currentLng = typeof window !== 'undefined' && window.localStorage?.getItem(LANGUAGE_KEY);
    const toStore = currentLng ? { ...userData, preferredLanguage: userData.preferredLanguage ?? currentLng } : userData;
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(toStore));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Safely retrieves user data from localStorage
 * @returns {object|null} - User data or null if not found/invalid
 */
export const getUserData = () => {
  try {
    const userDataStr = localStorage.getItem(USER_DATA_KEY);
    if (!userDataStr) {
      return null;
    }
    const userData = JSON.parse(userDataStr);
    if (!userData || typeof userData !== 'object') {
      return null;
    }
    return userData;
  } catch (error) {
    return null;
  }
};

/**
 * Removes user data from localStorage
 */
export const removeUserData = () => {
  try {
    localStorage.removeItem(USER_DATA_KEY);
  } catch (error) {
    // Ignore errors during cleanup
  }
};

/**
 * Persists preferred language for the current user (merged into stored userData).
 * Call after i18n.changeLanguage(). No-op if no user is stored.
 * @param {string} lng - Language code (e.g. 'pt-BR', 'en', 'es')
 */
export const setStoredUserPreferredLanguage = (lng) => {
  try {
    const userDataStr = localStorage.getItem(USER_DATA_KEY);
    if (!userDataStr) return;
    const userData = JSON.parse(userDataStr);
    if (!userData || typeof userData !== 'object') return;
    localStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...userData, preferredLanguage: lng }));
  } catch (error) {
    // ignore
  }
};

/**
 * Clears all authentication data
 */
export const clearAuthData = () => {
  removeToken();
  removeUserData();
  removeRedirectPath();
};

/**
 * Validates redirect path to prevent open redirect (e.g. //evil.com or javascript:).
 * Only allows relative paths starting with a single slash.
 * @param {string} path - Path to validate
 * @returns {boolean}
 */
const isValidRedirectPath = (path) => {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (trimmed === '' || trimmed === '/login') return false;
  if (!trimmed.startsWith('/')) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.includes(':')) return false;
  return true;
};

/**
 * Sets redirect path for post-login navigation
 * @param {string} path - Path to redirect to (must be app-relative, e.g. /dashboard)
 */
export const setRedirectPath = (path) => {
  try {
    if (isValidRedirectPath(path)) {
      localStorage.setItem(REDIRECT_PATH_KEY, path.trim());
    }
  } catch (error) {
    // Ignore errors
  }
};

/**
 * Gets and removes redirect path. Returns only if still valid (prevents serving stored malicious path).
 * @returns {string|null} - Redirect path or null
 */
export const getRedirectPath = () => {
  try {
    const path = localStorage.getItem(REDIRECT_PATH_KEY);
    if (path && isValidRedirectPath(path)) {
      localStorage.removeItem(REDIRECT_PATH_KEY);
      return path.trim();
    }
    if (path) {
      localStorage.removeItem(REDIRECT_PATH_KEY);
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Removes redirect path
 */
export const removeRedirectPath = () => {
  try {
    localStorage.removeItem(REDIRECT_PATH_KEY);
  } catch (error) {
    // Ignore errors
  }
};

/**
 * Checks if user is authenticated
 * @returns {boolean} - True if token exists and is valid
 */
export const isAuthenticated = () => {
  return !!getToken();
};
